export type FocusPanelScopeLevel = 'scope' | 'group' | 'task';

/**
 * Progress Alt's grid — the tabbed half of the pair it makes with the fullscreen page below.
 *
 * It answers to `progress`, the plainest segment, being the Progress panel the redesign ships. `Alt` survives in the code's own names from when
 * the two were built side by side, and goes away with the file-renaming pass.
 */
export const PROGRESS_ALT_PAGE = 'progress';

/**
 * URL path segment for each tabbed focus-panel page, in the order the tabs read.
 *
 * Tabbed here means "takes the three tabbed URL shapes", not "is currently offered in the tab row" —
 * Gallery is superseded and hidden, but still answers to its route so a reader who types it can
 * still reach it. The tab row itself is `FocusPanelTitlebar`'s own list.
 */
export const SCAN_FOCUS_PANEL_TAB_ORDER = [
  'overview',
  'edit',
  /** Superseded by `VideoToolboxPanel` in the scope editor; kept for legacy `/videos` URLs. */
  'videos',
  'photos',
  'measurements',
  PROGRESS_ALT_PAGE,
  'artifact',
] as const;

export type ScanFocusPanelTab = typeof SCAN_FOCUS_PANEL_TAB_ORDER[number];

/**
 * Progress Alt with one cell blown up: a page of its own so the cell it is showing is named by the
 * same group and task slots every other focused page uses, rather than by query params of its own.
 *
 * It is reached by opening a cell from the Progress Alt grid, never by a tab — it draws the very same
 * panel, magnified, so a tab beside Progress Alt would offer the reader a second door into one room.
 */
export const PROGRESS_ALT_FULLSCREEN_PAGE = 'progress-fullscreen';

/**
 * The whole-property floor plan: the Measurements panel's assembler view, where the rooms are placed
 * relative to each other level by level. A page of its own so the browser's history and a shared
 * link tell it apart from the room index the same panel draws at `measurements`. It is reached from
 * the view switch inside the Measurements panel, never by a tab.
 */
export const FLOOR_PLAN_PAGE = 'floor-plan';

/**
 * Every focus-panel page the router serves: the tabbed ones, then the ones only reachable from within
 * a panel. Both kinds take the same three URL shapes, so routing reads this and the tab row reads
 * `SCAN_FOCUS_PANEL_TAB_ORDER`.
 */
export const SCAN_FOCUS_PANEL_PAGE_ORDER = [
  ...SCAN_FOCUS_PANEL_TAB_ORDER,
  PROGRESS_ALT_FULLSCREEN_PAGE,
  FLOOR_PLAN_PAGE,
] as const;

export type ScanFocusPanelPage = typeof SCAN_FOCUS_PANEL_PAGE_ORDER[number];

/** The tab a page is drawn under: itself for a tabbed page, its owning panel's tab for a route-only page. */
export const getScanFocusPanelTab = (page: ScanFocusPanelPage): ScanFocusPanelTab =>
  page === PROGRESS_ALT_FULLSCREEN_PAGE ? PROGRESS_ALT_PAGE : page === FLOOR_PLAN_PAGE ? 'measurements' : page;

export type ScanFocusPanelScopeProps = {
  scopeLevel: FocusPanelScopeLevel;
  /** Turn-based projects replace the legacy project page with this workspace tab. */
  showProjectOverview?: boolean;
  activePanel: ScanFocusPanelPage;
  onSelectPanel: (panel: ScanFocusPanelPage) => void;
  groupTitle?: string;
  taskTitle?: string;
  selectedGroupId?: string;
  selectedTaskId?: string;
  /** Set only on a contractor route, where there is no real group being browsed. */
  contractorTitle?: string;
  selectedContractorId?: string;
  onSelectAll?: () => void;
  onSelectGroup?: (groupId: string) => void;
  onSelectTask?: (taskId: string) => void;
  onSelectContractor?: (contractorId: string) => void;
};

export const getScanFocusPanelScopeLevel = ({
  groupId,
  taskId,
}: {
  groupId?: string;
  taskId?: string;
}): FocusPanelScopeLevel => {
  if (groupId && taskId) return 'task';
  if (groupId) return 'group';
  return 'scope';
};
